"use client"

import { useState } from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet"
import { Menu, X } from "lucide-react"
import { CreditCounter } from "@/app/components/credit-counter"
import { useSession } from "@/app/context/AuthContext"

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false)
  const { data: session, status } = useSession()

  // Use the session status to determine if user is logged in
  const isLoggedIn = status === "authenticated"
  const user = session?.user

  const navLinks = [
    { name: "Gallery", href: "/gallery" },
    { name: "Designer", href: "/designer" },
    { name: "Pricing & Credits", href: "/pricing" },
    { name: "About Us", href: "/about" },
    { name: "Support", href: "/support" },
  ]

  return (
    <nav className="sticky top-0 z-50 w-full bg-white border-b border-gray-200 shadow-sm">
      <div className="container flex items-center justify-between h-16 px-4 mx-auto md:px-6">
        <Link href="/" className="flex items-center">
          <span className="text-2xl font-semibold text-[#40A1AB]">Roomvia</span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex md:items-center md:space-x-6">
          {navLinks.map((link) => (
            <Link key={link.name} href={link.href} className="text-gray-700 hover:text-[#40A1AB] transition-colors">
              {link.name}
            </Link>
          ))}
        </div>

        <div className="flex items-center space-x-4">
          {isLoggedIn && user && (
            <>
              <CreditCounter
                credits={user.creditBreakdown?.total || user.credits || 0}
                planRemaining={user.creditBreakdown?.planRemaining}
                walletCredits={user.creditBreakdown?.walletCredits}
                showBreakdown={true}
              />
            </>
          )}

          {isLoggedIn ? (
            <Link href="/dashboard">
              <Button variant="outline" className="hidden md:inline-flex bg-transparent">
                Dashboard
              </Button>
            </Link>
          ) : (
            <Link href="/login">
              <Button className="hidden md:inline-flex bg-[#40A1AB] hover:bg-[#3A919A] text-white">Sign In</Button>
            </Link>
          )}

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[80%] sm:w-[350px]">
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between py-4">
                  <span className="text-xl font-semibold text-[#40A1AB]">Roomvia</span>
                  <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)}>
                    <X className="h-5 w-5" />
                    <span className="sr-only">Close menu</span>
                  </Button>
                </div>
                <div className="flex flex-col space-y-4 py-4">
                  {navLinks.map((link) => (
                    <Link
                      key={link.name}
                      href={link.href}
                      className="text-gray-700 hover:text-[#40A1AB] transition-colors py-2"
                      onClick={() => setIsOpen(false)}
                    >
                      {link.name}
                    </Link>
                  ))}
                  {isLoggedIn ? (
                    <Link
                      href="/dashboard"
                      className="text-gray-700 hover:text-[#40A1AB] transition-colors py-2"
                      onClick={() => setIsOpen(false)}
                    >
                      Dashboard
                    </Link>
                  ) : (
                    <Link href="/login" className="inline-block mt-4" onClick={() => setIsOpen(false)}>
                      <Button className="bg-[#40A1AB] hover:bg-[#3A919A] text-white w-full">Sign In</Button>
                    </Link>
                  )}
                </div>
                <div className="mt-auto pt-4 border-t border-gray-200">
                  <div className="flex flex-col space-y-2">
                    <Link
                      href="/legal/privacy"
                      className="text-sm text-gray-500 hover:text-[#40A1AB]"
                      onClick={() => setIsOpen(false)}
                    >
                      Privacy Policy
                    </Link>
                    <Link
                      href="/legal/terms"
                      className="text-sm text-gray-500 hover:text-[#40A1AB]"
                      onClick={() => setIsOpen(false)}
                    >
                      Terms of Service
                    </Link>
                    <Link
                      href="/legal/ai-disclaimer"
                      className="text-sm text-gray-500 hover:text-[#40A1AB]"
                      onClick={() => setIsOpen(false)}
                    >
                      AI Disclaimer
                    </Link>
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  )
}
